/**
 * BigSplice Chat Results Table Component
 * Inline table display for query results in chat
 */

import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { AlertCircle } from 'lucide-react';

interface ChatResultsTableProps {
  columns: string[];
  rows: Record<string, unknown>[];
  totalRows?: number;
  truncated?: boolean;
  maxDisplayRows?: number;
}

export const ChatResultsTable: React.FC<ChatResultsTableProps> = ({
  columns,
  rows,
  totalRows = rows.length,
  truncated = false,
  maxDisplayRows = 10,
}) => {
  // Limit displayed rows
  const displayRows = rows.slice(0, maxDisplayRows);
  const hasMoreRows = rows.length > maxDisplayRows || truncated;

  // Format cell value for display
  const formatValue = (value: string | number | boolean | null): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (typeof value === 'number') {
      // Format large numbers with commas
      if (Math.abs(value) >= 1000) {
        return value.toLocaleString();
      }
      // Format decimals
      if (!Number.isInteger(value)) {
        return value.toFixed(2);
      }
    }
    return String(value);
  };

  // Truncate long strings
  const truncateString = (str: string, maxLength = 50): string => {
    if (str.length <= maxLength) return str;
    return str.slice(0, maxLength) + '...';
  };

  if (rows.length === 0) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-2">
        <AlertCircle className="h-4 w-4" />
        <span>No results found</span>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((col, index) => (
                  <TableHead
                    key={index}
                    className="whitespace-nowrap bg-muted/50 text-xs font-semibold"
                  >
                    {col}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayRows.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {columns.map((col, cellIndex) => {
                    const cell = row[col] as string | number | boolean | null;
                    return (
                      <TableCell
                        key={cellIndex}
                        className="text-xs py-1.5 whitespace-nowrap"
                        title={
                          typeof cell === 'string' && cell.length > 50
                            ? cell
                            : undefined
                        }
                      >
                        {truncateString(formatValue(cell))}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Row count info */}
      <div className="mt-2 text-xs text-muted-foreground flex items-center justify-between">
        <span>
          Showing {displayRows.length} of {totalRows.toLocaleString()} rows
        </span>
        {hasMoreRows && (
          <span className="text-yellow-600">
            Results truncated for display
          </span>
        )}
      </div>
    </div>
  );
};

export default ChatResultsTable;
